import { useFetch, useFetchCb } from '@gluedigital/ruse-fetch-extras'
import { useMemo } from 'react'
import { AUTH_URL } from 'src/constants/global'
import type { TokenInfo } from 'src/types/auth'

interface TokenListResponse {
  ok: boolean
  apiKeys: TokenInfo[]
}

export interface CreateApiKeyResponse {
  ok: boolean
  apiKey: TokenInfo
}

export interface CreateTokenPayload {
  name?: string
  expires?: string
}

export const useTokens = (): TokenInfo[] => {
  const data = useFetch<TokenListResponse>(AUTH_URL + '/auth/api-keys')
  return data?.apiKeys ?? []
}

export const useToken = (id?: string): TokenInfo | undefined => {
  const tokens = useTokens()
  return useMemo(() => {
    if (!id || !tokens) return undefined
    return tokens.find((token) => token.id === id)
  }, [tokens, id])
}

export const useTokenCRUD = () => {
  const doCreate = useFetchCb()
  const doDelete = useFetchCb()
  const doRefresh = useFetchCb()

  const refreshTokens = async () => {
    return await doRefresh(AUTH_URL + '/auth/api-keys')
  }

  const createToken = async (data?: CreateTokenPayload): Promise<TokenInfo> => {
    const response = (await doCreate(
      AUTH_URL + '/auth/api-keys?action=create',
      {
        method: 'POST',
        body: data
      }
    )) as CreateApiKeyResponse

    return response.apiKey
  }

  const revokeToken = async (id: string) => {
    await doDelete(AUTH_URL + '/auth/api-keys/' + id, {
      method: 'DELETE'
    })
    await refreshTokens()
  }

  return {
    createToken,
    revokeToken,
    refreshTokens
  }
}
